import React, { useContext, useState } from 'react'
import {
  Box,
  Checkbox,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import moment from 'moment'
import API from '../../../../services/API'
import { GlobalContext } from '../../../../App'

// New-starter onboarding checklist, seeded from Site Configuration →
// Employees → Applications when the application is accepted. Ticking a task
// stamps when it was completed; the card hides itself for employees with no
// checklist (e.g. those created before onboarding tasks were configured).
const OnboardingChecklist = ({ employee, userAccount, loadEmployee }) => {
  const [saving, setSaving] = useState(false)
  const { setToast } = useContext(GlobalContext)
  const checklist = employee?.onBoardingChecklist ?? []

  if (!checklist.length) return null

  const completedCount = checklist.filter((task) => task.completed).length
  const allDone = completedCount === checklist.length

  const toggleTask = (taskID) => {
    if (saving) return
    setSaving(true)
    const updated = checklist.map((task) =>
      task._id === taskID
        ? {
            ...task,
            completed: task.completed ? null : new Date(),
          }
        : task,
    )
    API.put('/employee', {
      _id: userAccount._id,
      updates: {
        // prefix must be re-sent — the endpoint nulls it when omitted
        prefix: userAccount.prefix,
        employee: { onBoardingChecklist: updated },
      },
    })
      .then((res) => {
        if (res.status === 200) {
          loadEmployee()
        } else {
          setToast({
            message: 'Unable to update the onboarding checklist',
            severity: 'error',
          })
        }
      })
      .finally(() => setSaving(false))
  }

  return (
    <Box component={Paper} padding={2}>
      <Stack gap={1}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          flexWrap="wrap"
        >
          <Typography variant="title3">Onboarding Checklist</Typography>
          <Chip
            size="small"
            sx={{ borderRadius: 1 }}
            color={allDone ? 'success' : 'info'}
            label={`${completedCount} of ${checklist.length} complete`}
          />
        </Stack>
        <LinearProgress
          variant="determinate"
          color={allDone ? 'success' : 'info'}
          value={(completedCount / checklist.length) * 100}
          sx={{ borderRadius: 1 }}
        />
        <Stack>
          {checklist.map((task) => (
            <Stack
              key={task._id}
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ cursor: 'pointer' }}
              onClick={() => toggleTask(task._id)}
            >
              <Checkbox
                checked={!!task.completed}
                disabled={saving}
                color="success"
                size="small"
              />
              <Typography
                variant="body2"
                sx={{
                  textDecoration: task.completed ? 'line-through' : 'none',
                  color: task.completed ? 'text.secondary' : 'text.primary',
                }}
              >
                {task.onboardingTask}
              </Typography>
              {task.completed && (
                <Typography variant="caption" color="text.secondary">
                  {moment(task.completed).format('Do MMM YYYY')}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  )
}

export default OnboardingChecklist
